import { useState } from 'react';
import { fill, Size, t, visitPictureAssetPath, type VisitPageAnswer, type VisitPhotoAsset } from '@bms2/shared';
import { Select } from '../../components/controls';
import { FormFrame } from '../../components/forms';
import { put } from './api';
import styles from './VisitPhotos.module.css';

/** The photo Link to Asset was pressed on: its file and the asset it stands on now, null where it stands on none. */
export interface LinkingPhoto {
  fileId: string;
  asset: VisitPhotoAsset | null;
}

/**
 * 🔒 Link to Asset (decision 13310; T3836, the owner's P3b): one asset picked from those the visit's answer offers, the
 * job's lines' assets first and then the nodes at the job's site, opening on the asset the photo stands on now. Save links
 * the photo to it through the visit's own route, which replaces the link it held, and hands the page's answer back. The
 * window is mounted only while open and keyed to its photo, so a reopened window never shows another photo's choice.
 */
export function VisitPhotoAssetWindow({ workOrderId, visitId, photo, choices, apiBaseUrl = '', onCancel, onLinked }: {
  workOrderId: string;
  visitId: string;
  photo: LinkingPhoto;
  choices: readonly VisitPhotoAsset[];
  apiBaseUrl?: string;
  onCancel: () => void;
  onLinked: (answer: VisitPageAnswer, linked: VisitPhotoAsset) => void;
}) {
  const [picked, setPicked] = useState(photo.asset?.id ?? '');
  const [refusal, setRefusal] = useState<string | null>(null);
  const save = async () => {
    setRefusal(null);
    const chosen = choices.find((choice) => choice.id === picked);
    if (!chosen) return setRefusal(t('serviceReport.photo.linkHint'));
    const answer = await put<VisitPageAnswer>(visitPictureAssetPath(workOrderId, visitId, photo.fileId), { assetNodeId: chosen.id }, apiBaseUrl);
    if (!answer.ok) return setRefusal(answer.message);
    onLinked(answer.data, chosen);
  };
  return (
    <FormFrame
      host="window"
      open
      title={t('serviceReport.photo.linkAsset')}
      onCancel={onCancel}
      dataForm="visit-photo-asset"
      typed={picked !== (photo.asset?.id ?? '')}
      act={{ label: t('modal.save'), onAct: () => save() }}
      refusal={refusal}
      onDismissRefusal={() => setRefusal(null)}
    >
      <p className={styles.viewerBy}>{t(choices.length === 0 ? 'serviceReport.photo.linkNone' : 'serviceReport.photo.linkHint')}</p>
      {choices.length === 0 ? null : (
        <Select
          size={Size.Large}
          label={t('serviceReport.photo.linkField')}
          value={picked}
          onChange={setPicked}
          options={choices.map((choice) => ({ value: choice.id, label: choice.name }))}
          dataAttribute={{ 'data-visit-photo-asset-field': photo.fileId }}
          required
        />
      )}
    </FormFrame>
  );
}

/** 🔒 Decision 13310: the line naming the asset a photo stands on, read under the opened picture. */
export function linkedAssetLine(asset: VisitPhotoAsset | null | undefined): string | null {
  return asset ? fill(t('serviceReport.photo.linkedTo'), { name: asset.name }) : null;
}
